import generatePicker from 'antd/es/date-picker/generatePicker';
import type { PickerProps } from 'antd/es/date-picker/generatePicker';
import type { Moment } from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { forwardRef, useEffect, useId, useMemo, useRef } from 'react';
import styled from 'styled-components';
import useT from '@tools/ui-hooks/useT';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import {
  InputClearAction,
  inputFieldBorderStyle,
  InputHelpText,
  InputLabel
} from '../_shared/InputField';

const AntDatePicker = generatePicker<Moment>(momentGenerateConfig);

type AntDatePickerFieldProps = Pick<
  PickerProps<Moment>,
  | 'value'
  | 'defaultValue'
  | 'placeholder'
  | 'disabled'
  | 'onChange'
  | 'onFocus'
  | 'onBlur'
  | 'className'
  | 'id'
  | 'name'
  | 'aria-label'
  | 'aria-labelledby'
>;

export type DateInputProps = AntDatePickerFieldProps &
  Partial<{
    readOnly: boolean;
    status: 'error' | 'default';
    format: string;
    label: string;
    optional: boolean;
    infoIcon: boolean;
    infoText: string;
    helpText: string;
    clearAction: boolean;
    onClear: () => void;
  }>;

const NON_CHARACTER_KEYS = new Set([
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Tab',
  'Enter',
  'Escape',
  'Home',
  'End'
]);

const ALPHABETIC_FORMAT_TOKEN_PATTERN = /MMMM|MMM|dddd|ddd|Do|A|a/;

const getDateKeyPattern = (dateFormat: string) => {
  const separators = Array.from(
    new Set(dateFormat.replace(/[a-zA-Z0-9]/g, '').split(''))
  );
  const escapedSeparators = separators
    .map((separator) => `\\${separator}`)
    .join('');
  const letters = ALPHABETIC_FORMAT_TOKEN_PATTERN.test(dateFormat)
    ? 'a-zA-Z'
    : '';
  return new RegExp(`^[0-9${letters}${escapedSeparators}]$`);
};

const createKeyDownHandler =
  (dateKeyPattern: RegExp) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || NON_CHARACTER_KEYS.has(e.key)) return;
    if (e.key.length === 1 && !dateKeyPattern.test(e.key)) {
      e.preventDefault();
    }
  };

const createPasteHandler =
  (dateKeyPattern: RegExp) => (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (Array.from(pastedText).some((char) => !dateKeyPattern.test(char))) {
      e.preventDefault();
    }
  };

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedAntDatePicker = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  any
>((props, ref) => <AntDatePicker {...props} ref={ref} />);

const StyledDatePicker = styled(ForwardedAntDatePicker)<{
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
}>`
  &&.ant-picker {
    ${inputFieldBorderStyle}
    border-radius: ${theme.dime.radius.m};
    padding: 0 ${theme.dime.spacing.l};
    height: ${theme.dime.spacing['3xl']};
    box-shadow: none;
    width: 100%;
    margin-bottom: ${theme.dime.spacing.m};
  }

  &&.ant-picker-disabled {
    cursor: not-allowed;
  }

  &&.ant-picker-disabled input::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }

  &&.ant-picker input {
    cursor: ${({ $disabled, $readOnly }) =>
      $disabled || $readOnly ? 'not-allowed' : 'text'};
    background-color: transparent;
  }
`;

export const DateInput = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  DateInputProps
>(
  (
    {
      value,
      defaultValue,
      placeholder,
      disabled = false,
      readOnly = false,
      onChange,
      onFocus,
      onBlur,
      className = '',
      id,
      name,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      status = 'default',
      format = 'MM/DD/YYYY',
      label,
      optional = false,
      infoIcon = false,
      infoText,
      helpText,
      clearAction = false,
      onClear,
      ...rest
    },
    ref
  ) => {
    const { t } = useT();
    const hasError = status === 'error';
    const resolvedPlaceholder = placeholder ?? t('dateInput.placeholder');
    const dateKeyPattern = useMemo(() => getDateKeyPattern(format), [format]);
    const containerRef = useRef<HTMLDivElement>(null);
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const labelId = `${inputId}-label`;

    const handleKeyDown = useMemo(
      () => createKeyDownHandler(dateKeyPattern),
      [dateKeyPattern]
    );
    const handlePaste = useMemo(
      () => createPasteHandler(dateKeyPattern),
      [dateKeyPattern]
    );

    useEffect(() => {
      const inputEl = containerRef.current?.querySelector('.ant-picker input');
      if (!(inputEl instanceof HTMLInputElement)) return undefined;

      const stripTitle = () => {
        if (inputEl.hasAttribute('title')) inputEl.removeAttribute('title');
      };

      stripTitle();
      const observer = new MutationObserver(stripTitle);
      observer.observe(inputEl, { attributes: true, attributeFilter: ['title'] });

      return () => observer.disconnect();
    }, []);

    return (
      <InputWrapper ref={containerRef} className={className}>
        {!!label && (
          <InputLabel
            id={labelId}
            label={label}
            optional={optional}
            infoIcon={infoIcon}
            infoText={infoText}
          />
        )}
        <StyledDatePicker
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          inputReadOnly={readOnly}
          open={readOnly ? false : undefined}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          inputMode="numeric"
          id={inputId}
          name={name}
          aria-label={ariaLabel}
          aria-labelledby={label ? labelId : ariaLabelledBy}
          format={format}
          allowClear={false}
          suffixIcon={
            <IconWrap
              type={IconType.Calendar}
              size={IconSize.Small}
              disabled={disabled || readOnly}
              style={
                disabled || readOnly
                  ? undefined
                  : { color: theme.dime.colors.icon.primary }
              }
            />
          }
          $error={hasError}
          $disabled={disabled}
          $readOnly={readOnly}
          {...rest}
        />
        {!!helpText && <InputHelpText text={helpText} hasError={hasError} />}
        {!!clearAction && !disabled && !readOnly && (
          <InputClearAction onClear={onClear} />
        )}
      </InputWrapper>
    );
  }
);

DateInput.displayName = 'DateInput';
